import { useState } from 'react';
import { useGoLive } from '@/hooks/useLiveStreams';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from '@/components/ui/drawer';
import { mobileDrawerContentStyle, MOBILE_DRAWER_OVERLAY_CLASS } from '@/lib/drawerStyles';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/useToast';
import { X } from 'lucide-react';

interface GoLiveModalProps {
  open: boolean;
  onClose: () => void;
}

export function GoLiveModal({ open, onClose }: GoLiveModalProps) {
  const { user } = useCurrentUser();
  const isMobile = useIsMobile();
  const { goLive, isPending } = useGoLive();
  const { toast } = useToast();

  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [streamingUrl, setStreamingUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [tagsInput, setTagsInput] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !streamingUrl.trim()) {
      toast({ title: 'Missing fields', description: 'Title and streaming URL are required.', variant: 'destructive' });
      return;
    }

    goLive(
      {
        title: title.trim(),
        summary: summary.trim(),
        streamingUrl: streamingUrl.trim(),
        imageUrl: imageUrl.trim() || undefined,
        tags: tagsInput.split(',').map(t => t.trim()).filter(Boolean),
      },
      {
        onSuccess: () => {
          toast({ title: '🔴 You are live!', description: 'Your stream is now visible on Goblinbox.' });
          setTitle(''); setSummary(''); setStreamingUrl(''); setImageUrl(''); setTagsInput('');
          onClose();
        },
        onError: (err) => {
          toast({ title: 'Failed to go live', description: String(err), variant: 'destructive' });
        },
      },
    );
  };

  if (!user) return null;

  /* ── Shared form body ── */
  const formBody = (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Info callout */}
      <div
        className="rounded-lg p-3 text-xs"
        style={{ background: 'rgba(58,158,58,0.1)', border: '1px solid var(--gb-accent)', color: 'var(--text-secondary)' }}
      >
        <strong style={{ color: 'var(--gb-accent)' }}>How it works:</strong> Provide an RTMP / HLS streaming URL from
        your encoder (OBS, Streamlabs, etc.) or a CDN. Goblinbox publishes your stream info to the Nostr network via{' '}
        <strong>NIP-53</strong> so others can discover and watch it.
      </div>

      {/* Title */}
      <div className="space-y-1.5">
        <Label style={{ color: 'var(--text-primary)' }}>
          Stream Title <span style={{ color: '#f44336' }}>*</span>
        </Label>
        <Input
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="What are you streaming today?"
          required
          style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}
        />
      </div>

      {/* Streaming URL */}
      <div className="space-y-1.5">
        <Label style={{ color: 'var(--text-primary)' }}>
          Streaming URL <span style={{ color: '#f44336' }}>*</span>
        </Label>
        <Input
          value={streamingUrl}
          onChange={e => setStreamingUrl(e.target.value)}
          placeholder="https://your-cdn.com/stream.m3u8"
          type="url"
          required
          style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}
        />
        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
          HLS (.m3u8) or direct video URL. Get this from your CDN, Owncast, or Cloudflare Stream.
        </p>
      </div>

      {/* Description */}
      <div className="space-y-1.5">
        <Label style={{ color: 'var(--text-primary)' }}>Description</Label>
        <Textarea
          value={summary}
          onChange={e => setSummary(e.target.value)}
          placeholder="Tell viewers what this stream is about..."
          rows={3}
          style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)', resize: 'none' }}
        />
      </div>

      {/* Thumbnail */}
      <div className="space-y-1.5">
        <Label style={{ color: 'var(--text-primary)' }}>Thumbnail URL</Label>
        <Input
          value={imageUrl}
          onChange={e => setImageUrl(e.target.value)}
          placeholder="https://example.com/thumbnail.jpg"
          type="url"
          style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}
        />
      </div>

      {/* Tags */}
      <div className="space-y-1.5">
        <Label style={{ color: 'var(--text-primary)' }}>Tags</Label>
        <Input
          value={tagsInput}
          onChange={e => setTagsInput(e.target.value)}
          placeholder="gaming, bitcoin, music"
          style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}
        />
        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Comma-separated tags.</p>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 py-2 rounded font-medium text-sm transition-colors"
          style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isPending}
          className="flex-1 flex items-center justify-center gap-2 py-2 rounded font-medium text-sm text-white transition-all hover:opacity-90 disabled:opacity-50"
          style={{ background: '#f44336' }}
        >
          {isPending ? (
            'Publishing...'
          ) : (
            <>
              <span className="w-2 h-2 rounded-full bg-white" style={{ animation: 'pulse 1.5s ease-in-out infinite' }} />
              Go Live
            </>
          )}
        </button>
      </div>
    </form>
  );

  /* ── Mobile: Drawer ── */
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={v => !v && onClose()} dismissible>
        <DrawerContent
          className="flex flex-col"
          overlayClassName={MOBILE_DRAWER_OVERLAY_CLASS}
          style={mobileDrawerContentStyle}
        >
          <DrawerHeader
            className="shrink-0 relative pb-2"
            style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--gb-border)' }}
          >
            <DrawerClose asChild>
              <button
                className="absolute right-4 top-4 rounded-full p-1"
                style={{ color: 'var(--text-secondary)' }}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </DrawerClose>
            <DrawerTitle
              className="flex items-center gap-2 pt-1"
              style={{ color: 'var(--text-primary)' }}
            >
              <span className="flex items-center justify-center w-6 h-6 rounded-full" style={{ background: '#f44336' }}>
                <span className="block w-2 h-2 rounded-full bg-white" />
              </span>
              Go Live
            </DrawerTitle>
          </DrawerHeader>
          <div
            className="flex-1 overflow-y-auto p-4"
            style={{
              background: 'var(--bg-secondary)',
              paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))',
              color: 'var(--text-primary)',
            }}
          >
            {formBody}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  /* ── Desktop: Dialog ── */
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent
        className="max-w-lg w-full max-h-[92dvh] flex flex-col overflow-hidden"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base" style={{ color: 'var(--text-primary)' }}>
            <span className="flex items-center justify-center w-6 h-6 rounded-full" style={{ background: '#f44336' }}>
              <span className="block w-2 h-2 rounded-full bg-white" />
            </span>
            Go Live
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto mt-2">
          {formBody}
        </div>
      </DialogContent>
    </Dialog>
  );
}
